import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useGasPrice } from "@/hooks/useGasPrice";
import { useSwapSettingStorage } from "@/hooks/Storage/useSwapSettingStorage";
import Modal from "../Modal";
import { SUPPORT_CHAINS } from "@/libs/PrivyProvider";
import Image from "next/image";
import { NATIVE } from "@/constants/tokens";
import { GET } from "@/libs/Networks";
import { useAccount } from "@/hooks/useAccount";
import { useWallets } from "@privy-io/react-auth";
import clsx from "clsx";

interface OpenChainModalProps {
  isOpen: boolean;
  onDismiss: () => void;
}

export default function OpenChainModal({
  isOpen,
  onDismiss,
}: OpenChainModalProps) {
  const { currentGasPrice } = useGasPrice();
  const { gasPrice, setSlippage, setGasPrice } = useSwapSettingStorage();

  const gasPrices: number[] = useMemo(() => {
    return [currentGasPrice, currentGasPrice * 1.2, currentGasPrice * 1.4];
  }, [currentGasPrice]);

  useEffect(() => {
    const prepareGasPrices = async () => {
      if (setGasPrice && (!gasPrice || !(gasPrice.value > 0))) {
        setGasPrice({ value: gasPrices[0], option: 0 });
      }
    };
    prepareGasPrices();
  }, [currentGasPrice, gasPrices, gasPrice, setGasPrice]);

  const wrappedOnDismiss = useCallback(() => {
    onDismiss();
  }, [onDismiss]);

  const [liquidity, setLiquidity] = useState(false);
  const [showOpenCustomizeRouting, setShowOpenCustomizeRouting] =
    useState(false);

  const handleOnDetailClick = useCallback(() => {
    setShowOpenCustomizeRouting((current) => {
      return !current;
    });
  }, []);

  const handleOnBack = useCallback(() => {
    setLiquidity(false);
  }, []);

  const { address, chain } = useAccount();
  const { wallets } = useWallets();

  const native = useMemo(() => NATIVE[GET(chain?.id)], [chain?.id]);

  const switchNetwork = useCallback(
    async (chainId: number) => {
      await wallets[0].switchChain(chainId);
    },
    [wallets]
  );

  return (
    <Modal
      title={"Switch Networks"}
      onBack={liquidity ? handleOnBack : undefined}
      isShow={isOpen}
      onClose={wrappedOnDismiss}
      background="sm:bg-opacity-25 bg-bitzy-white"
    >
      <div className="flex flex-col gap-2 h-full w-full">
        {SUPPORT_CHAINS.map((d) => {
          return (
            <>
              <div
                className={clsx(
                  chain?.id === d.id && "bg-bitzy",
                  "flex justify-between gap-2 p-2 border-b border-white/20 last:border-0 cursor-pointer group hover:bg-bitzy"
                )}
                onClick={() => switchNetwork(d.id)}
              >
                <div className="flex items-center gap-2">
                  <Image
                    src={d.iconUrl}
                    alt={d.id.toString()}
                    width={28}
                    height={28}
                  />

                  <div
                    className={clsx(
                      chain?.id === d.id
                        ? "text-bitzy-theme-btw font-semibold"
                        : "text-bitzy-white group-hover:text-bitzy-theme-btw",
                      "text-sm md:text-base"
                    )}
                  >
                    {d.name}
                  </div>
                </div>

                {chain?.id === d.id && (
                  <div className="flex items-center gap-2">
                    <div
                      className={clsx(
                        chain?.id === d.id
                          ? "text-bitzy-theme-btw"
                          : "text-bitzy-white",
                        "text-sm"
                      )}
                    >
                      Connected
                    </div>
                    <div className="w-2 h-2 rounded-full bg-bitzy-green"></div>
                  </div>
                )}
              </div>
            </>
          );
        })}
      </div>
    </Modal>
  );
}
